import { Link } from 'react-router-dom';

import StatsSection from '../../../common/components/stats-section';
import { RECIPIENT_PRODUCTION_CARDS, RECIPIENT_RATE_ROWS } from '../../../core/constants/kpis';
import { HISTORY_OUTCOME, PREGNANCY_STATE } from '../../../core/constants/reproduction';
import { formatDateOnly, relativeDayLabel } from '../../../core/utils/date';
import { useRecipientDetail } from '../contexts/recipient-detail';
import { statusLabel } from '../utils/status';

import '../styles/detail.css';

// Dashboard de UNA receptora del cliente — endpoint REAL (swap Q-118 §1
// cerrado en D-010): GET public/recipients/:id?view=full, adaptado en
// api/provisional/recipient-detail.js (envelope desenvuelto). 404 = receptora
// fuera de la huella (anti-enumeración) → estado amable. Sin "Eventos
// médicos": el contrato público los excluye (operación interna).
// Los indicadores los pinta el StatsSection compartido (common/, D-006).

function BackLink() {
    return <Link to="/parcerias" className="parceria-detail__back">← Mis parcerías</Link>;
}

function Field({ label, children }) {
    return (
        <div className="parceria-detail__field">
            <span className="parceria-detail__field-label">{label}</span>
            <span className="parceria-detail__field-value">
                {children || <span className="parceria-detail__muted">—</span>}
            </span>
        </div>
    );
}

function ToneBadge({ tone, children }) {
    return <span className={`parceria-detail__badge parceria-detail__badge--${tone || 'open'}`}>{children}</span>;
}

function PregnancyState({ current }) {
    if (!current) {
        return <p className="parceria-detail__muted">Sin información de estado.</p>;
    }

    if (current.available) {
        return (
            <div className="parceria-detail__state-row">
                <ToneBadge tone="good">Disponible</ToneBadge>
                <span className="parceria-detail__muted">Libre para una nueva transferencia.</span>
            </div>
        );
    }

    const pregnancy = current.pregnancy;
    if (!pregnancy) {
        return <p className="parceria-detail__muted">Sin preñez activa registrada.</p>;
    }

    const state = PREGNANCY_STATE[pregnancy.state];
    const nextDx = pregnancy.next_dx;
    const forecast = pregnancy.forecast;

    return (
        <div className="parceria-detail__pregnancy">
            <div className="parceria-detail__state-row">
                <ToneBadge tone={state?.tone}>{state?.label || pregnancy.state}</ToneBadge>
                {pregnancy.estimated && (
                    <span className="parceria-detail__muted">
                        Parto estimado: {formatDateOnly(pregnancy.estimated)} ({relativeDayLabel(pregnancy.estimated)})
                    </span>
                )}
            </div>

            {nextDx && (
                <div className="parceria-detail__next-dx">
                    <span className="parceria-detail__field-label">Próximo diagnóstico</span>
                    <span>
                        Dx {nextDx.number} — {formatDateOnly(nextDx.scheduled_for)} ({relativeDayLabel(nextDx.scheduled_for)})
                        {nextDx.due && <ToneBadge tone="bad">Vencido</ToneBadge>}
                    </span>
                </div>
            )}

            {forecast && (
                <div className="parceria-detail__forecast">
                    <Field label="Dx 1 estimado">{formatDateOnly(forecast.dx1_estimated)}</Field>
                    <Field label="Dx 2 estimado">{formatDateOnly(forecast.dx2_estimated)}</Field>
                    <Field label="Parto estimado">{formatDateOnly(forecast.parto_estimated)}</Field>
                </div>
            )}
        </div>
    );
}

function HistoryItem({ item }) {
    const outcome = HISTORY_OUTCOME[item.outcome];
    const donor = item.cruce?.donor?.name;
    const sire = item.cruce?.sire?.name;

    return (
        <li className="parceria-detail__history-item">
            <div className="parceria-detail__history-main">
                <span className="parceria-detail__history-date">{formatDateOnly(item.te_date)}</span>
                <span className="parceria-detail__history-cross">
                    {donor && sire ? `${donor} × ${sire}` : donor || sire || 'Cruce sin datos'}
                </span>
                <span className="parceria-detail__muted">
                    {item.embryo?.code && `Embrión ${item.embryo.code}`}
                    {item.farm?.name && ` · ${item.farm.name}`}
                </span>
            </div>
            {outcome && <ToneBadge tone={outcome.tone}>{outcome.label}</ToneBadge>}
        </li>
    );
}

export default function ParceriaDetail({ recipientId }) {
    const { data: recipient, isLoading, isError, error } = useRecipientDetail(
        recipientId != null ? Number(recipientId) : null,
    );

    if (isLoading) {
        return <div className="parceria-detail__state">Cargando receptora...</div>;
    }

    if (isError) {
        return (
            <section className="parceria-detail">
                <BackLink />
                <p className="parceria-detail__state parceria-detail__state--error">
                    {error?.status === 404
                        ? 'Receptora no encontrada.'
                        : (error?.message || 'No se pudo cargar la receptora.')}
                </p>
            </section>
        );
    }

    if (!recipient) {
        return (
            <section className="parceria-detail">
                <BackLink />
                <p className="parceria-detail__state">Receptora no encontrada.</p>
            </section>
        );
    }

    return (
        <section className="parceria-detail">
            <header className="parceria-detail__header">
                <BackLink />
                <div className="parceria-detail__title-row">
                    <h2 className="parceria-detail__title">
                        {recipient.name || `Receptora #${recipient.id}`}
                    </h2>
                    <span className={`parceria-detail__status parceria-detail__status--${recipient.status}`}>
                        {statusLabel(recipient.status)}
                    </span>
                </div>
                <div className="parceria-detail__meta">
                    <span>Nº {recipient.number}</span>
                    {recipient.farm?.name && <span>{recipient.farm.name}</span>}
                    {recipient.current_location?.name
                        && recipient.current_location.name !== recipient.farm?.name
                        && <span>Ubicación actual: {recipient.current_location.name}</span>}
                </div>
            </header>

            <section className="parceria-detail__card">
                <h3 className="parceria-detail__section-title">Estado reproductivo</h3>
                <PregnancyState current={recipient.current} />
            </section>

            <section className="parceria-detail__card">
                <h3 className="parceria-detail__section-title">Indicadores</h3>
                <StatsSection
                    production={recipient.stats?.production}
                    productionConfig={RECIPIENT_PRODUCTION_CARDS}
                    funnel={recipient.stats?.funnel}
                    rates={recipient.stats?.rates}
                    rateRows={RECIPIENT_RATE_ROWS}
                />
            </section>

            <section className="parceria-detail__card">
                <h3 className="parceria-detail__section-title">Historial de transferencias</h3>
                {recipient.history.length === 0 ? (
                    <p className="parceria-detail__muted">Sin transferencias registradas.</p>
                ) : (
                    <ul className="parceria-detail__history">
                        {recipient.history.map((item, i) => (
                            <HistoryItem key={item.transfer_id ?? i} item={item} />
                        ))}
                    </ul>
                )}
            </section>
        </section>
    );
}
